import React, { useMemo } from 'react';

import {
  useGetCard,
  useGetFace,
  useGetFaces,
  useGetLists,
} from '@vlabs/api/artemy/client';
import type { CardId, EventTopMatchFace } from '@vlabs/api/artemy/model';
import { proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon, InfoCircleIcon, PinFilledIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { isUUID } from '@vlabs/shared/validators';
import { Control, FaceSample, Tooltip } from '@vlabs/uikit';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import { routes } from '@vlapps/point/src/app/routes';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { CopyableText } from '@vlabs/monokit/shared/ui/copyable-text';

import st from './EventTopMatchInfo.module.sass';

interface EventTopMatchInfoProps {
  cardId?: CardId;
  topMatch: EventTopMatchFace;
}

// В point3 top_match всегда типа face (event-матч бывает только в старом point),
// поэтому компонент работает только с лицом и его карточкой.
function EventTopMatchInfo({
  cardId = undefined,
  topMatch,
}: EventTopMatchInfoProps): React.JSX.Element {
  const { t } = useTranslation();

  const faceId = topMatch?.face_id;
  const getFace = useGetFace(faceId ?? '', undefined, {
    query: { enabled: !!faceId },
  });
  const matchFace = getFace.data;

  const getCard = useGetCard(cardId ?? '', {}, { query: { enabled: !!cardId } });
  const cardData = getCard.data;

  const getCardFaces = useGetFaces(
    { card_ids: cardId ? [cardId] : [] },
    { query: { enabled: !!cardId } },
  );

  // top_match.label: UUID — список, по которому произошло совпадение; иначе — параметр сравнения
  const label = topMatch?.label;
  const isLabelList = isUUID(label);
  const getLists = useGetLists(
    { list_ids: isLabelList ? [label] : [] },
    { query: { enabled: isLabelList } },
  );
  const matchedList = getLists.data?.lists?.find(({ list_id }) => list_id === label);

  const orderedCardFaces = useMemo(() => {
    const cardFaces = getCardFaces.data?.faces ?? [];
    const mainCardFace = cardFaces.find(({ face_id }) => face_id === cardData?.main_face_id);

    if (!mainCardFace) return cardFaces;

    return [
      mainCardFace,
      ...cardFaces.filter(({ face_id }) => face_id !== mainCardFace.face_id),
    ];
  }, [cardData?.main_face_id, getCardFaces.data?.faces]);

  const renderMatchField = (): React.JSX.Element => {
    if (isLabelList) {
      return (
        <React.Fragment>
          <div className={st.Header}>
            {t('lunapoint:карточка персоны.список')}
            <Tooltip
              tooltipContent={t('lunapoint:карточка персоны.подсказка списка')}
              tooltipProps={{ place: 'bottom' }}
            >
              <span className={st.InfoIcon}>
                <InfoCircleIcon />
              </span>
            </Tooltip>
          </div>
          <div
            className={st.Value}
            data-testid="infoTab.matchSection.listId"
          >
            <Control.Link
              className={st.ListLink}
              to={generatePath(routes.lists.read, { listId: label })}
            >
              {matchedList?.user_data ?? label}
            </Control.Link>
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <div className={st.Header}>
          {t('lunapoint:карточка персоны.параметр сравнения')}
        </div>
        <div
          className={st.Value}
          data-testid="infoTab.matchSection.matchLabel"
        >
          {label ?? '-'}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className={st.Root}>
      <div className={st.Data}>
        <FaceSample
          className={st.Sample}
          data-testid="infoTab.matchSection.matchSample"
          size="small"
          src={proxyArtemyUrl(matchFace?.avatar)}
        />

        <div className={st.Details}>
          <SimilarityTag
            data-testid="infoTab.matchSection.similarityTagType"
            icon={<DetectionIcon />}
            size="large"
            title={t('lunapoint:карточка персоны.карточка')}
            value={topMatch.similarity}
          />

          <div className={st.Fields}>
            <div className={st.Header}>{t('lunapoint:карточка персоны.фио')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.matchSection.fio"
            >
              {(cardData?.user_data?.fio as string | undefined) ?? '-'}
            </div>

            {renderMatchField()}

            <div className={st.Header}>{t('lunapoint:карточка персоны.ID карточки')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.matchSection.externalId"
            >
              {cardId ? <CopyableText value={cardId} /> : '-'}
            </div>
          </div>
        </div>
      </div>

      {orderedCardFaces.length > 0 && (
        <React.Fragment>
          <div className={st.Divider} />

          <div className={st.Subsection}>
            <p className={st.SubsectionTitle}>
              {t('lunapoint:карточка персоны.другие лица карточки')}
            </p>

            <div className={st.FacesGrid}>
              {orderedCardFaces.map((face) => (
                <div
                  className={st.FaceItem}
                  key={face.face_id}
                >
                  <FaceSample
                    className={st.SmallSample}
                    data-testid="infoTab.cardSection.cardFaceSample"
                    placeholder={(
                      <BaseSamplePlaceholder
                        showTextOnPlaceholder={false}
                        size="small"
                      />
                    )}
                    src={proxyArtemyUrl(face?.avatar)}
                  />

                  {face.face_id === cardData?.main_face_id && (
                    <div className={st.MainCardFaceMarker}>
                      <PinFilledIcon />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export { EventTopMatchInfo };
